import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, unwrap } from '../../api/client';
import { queryKeys } from '../../api/queryKeys';
import type { components } from '../../api/schema';

export type ChurchClientConfig = components['schemas']['ChurchClientConfigDto'];

/**
 * The church's presentation-affecting settings, fetched once per session
 * (docs/07-implementation-roadmap.md F1.9, docs/04 §3b).
 *
 * **This is the only settings endpoint the shell may depend on.** `GET /church`
 * is STAFF/SUPER_ADMIN — a PASTOR or REGION_COORDINATOR calling it gets a 403 —
 * so anything outside the settings screen that reaches for the full church
 * record breaks two of the four roles the day it ships. `client-config` is open
 * to any authenticated user precisely so a client can lay itself out.
 *
 * `staleTime: Infinity`: these keys change when a Super Admin edits them, which
 * is a handful of times in the system's life. Refetching them on a schedule
 * would be a request per navigation for a value that has not moved since
 * installation; the settings screen invalidates the key when it writes one.
 */
export function useClientConfig(): UseQueryResult<ChurchClientConfig> {
  return useQuery({
    queryKey: queryKeys.church.clientConfig(),
    queryFn: () => unwrap(api.GET('/api/v1/church/client-config')),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
